import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';
import { CONTAINER_WIDTHS, GUIDED_CONNECTION_WIDTHS } from '@hookdeck/theme';
import { Event } from '../../../../../../../../typings/Event.interface';
import { Request } from '../../../../../../../../typings/Request.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { getErrorCodeLabels } from '../../../../../utils/error-codes';
import { cause_filter_breakdown } from '../../../../../utils/rejection-causes';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Skeleton from '../../../../common/base/Skeleton';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import { HeadersBlock } from '../../../../common/Data/Headers';
import DisplayDate from '../../../../common/DisplayDate';
import { Div } from '../../../../common/helpers/StyledUtils';
import JSONBrowser from '../../../../common/JSONBrowser';
import { Status } from '../../../../common/Status';
import Table from '../../../../common/Table/Table';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { EventListResults } from '../../../../hooks/useEventList';
import { RequestListResult } from '../../../../hooks/useRequestList';
import { DashboardContext } from '../../DashboardContext';
import { EventMetadata } from '../../Event/EventOverview';
import { APIList } from '../../../../../../../../typings/API.interface';
import { IgnoredEvent } from '../../../../../../../../typings/IgnoredEvent.interface';
const PreviewEvents: React.FC<{
  context: {
    webhook: APIWebhook;
    events_list: EventListResults;
    requests_list: RequestListResult;
  };
  mutateContext: (context: {
    event?: Event;
    request?: Request;
    webhook?: APIWebhook;
  }) => void;
}> = ({
  context: {
    webhook,
    events_list,
    requests_list
  },
  mutateContext
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  const [active_tab, setActiveTab] = useState<'headers' | 'request' | 'res_body' | 'metadata'>('headers');
  const {
    addToast
  } = useToasts();
  const [selected_event_id, setSelectedEventId] = useState<string | null>(null);
  const selected_event = selected_event_id?.indexOf('req_') === 0 ? null : events_list?.events.find(event => event.id === (selected_event_id || events_list?.events[0]?.id));
  const {
    data: attempt_results,
    mutate: mutateAttempts
  } = useSWR(selected_event && APIMethodKeys.attempts.list({
    event_id: selected_event.id
  }), () => HookdeckAPI.attempts.list({
    event_id: selected_event?.id
  }));
  const attempt_to_fetch = attempt_results?.models[0];
  const {
    data: attempt
  } = useSWR(attempt_to_fetch && APIMethodKeys.attempts.get(attempt_to_fetch.id), () => HookdeckAPI.attempts.get(attempt_to_fetch?.id as string));

  // No bulk GET /ignored-events on the API; use GET /requests/:id/ignored_events per request.
  const sorted_ignored_request_ids = (() => {
    const ids = requests_list?.requests.filter(request => request.ignored_count && request.ignored_count > 0).map(request_0 => request_0.id) ?? [];
    return Array.from(new Set(ids)).sort();
  })();
  const ignored_events_swr_key = sorted_ignored_request_ids.length > 0 ? ['guided-preview-ignored', ...sorted_ignored_request_ids] as const : null;
  const {
    data: ignored_events
  } = useSWR(ignored_events_swr_key, async (): Promise<APIList<IgnoredEvent>> => {
    const lists = await Promise.all(sorted_ignored_request_ids.map(request_id => HookdeckAPI.requests.listIgnoredEvents(request_id)));
    const models = lists.flatMap(list => list.models);
    return {
      models,
      count: models.length,
      pagination: lists[0]?.pagination ?? {
        order_by: 'id',
        dir: 'desc' as const,
        limit: 250
      }
    };
  });
  const select_ignored_event = ignored_events?.models.find(event_0 => event_0.id === selected_event_id);
  useEffect(() => {
    if (attempt_results !== undefined) {
      mutateAttempts();
    }
  }, [selected_event]);
  const rows = [...(events_list?.events || []), ...(ignored_events?.models || [])].sort((a, b) => a.created_at > b.created_at ? -1 : 1).map(event_or_ignored_event => {
    if ('cause' in event_or_ignored_event) {
      const ignored_event = event_or_ignored_event;
      return {
        id: ignored_event.id,
        selected: !!selected_event_id && ignored_event.id === selected_event_id,
        fields: [<DisplayDate key={ignored_event.id} date={ignored_event.created_at} show_timezone={false} />, <Div key={`${ignored_event.id}-status`} flex={{
          gap: 2,
          justify: 'space-between',
          align: 'center'
        }}>
                <Badge icon="block" neutral mono>
                  {cause_filter_breakdown[ignored_event.cause](ignored_event).label}
                </Badge>
                <Button.Permission role="member" ghost icon="retry" muted small onClick={e => {
            setSelectedEventId(ignored_event.id);
            e.stopPropagation();
            HookdeckAPI.requests.retry(ignored_event.request_id, [ignored_event.webhook_id]).then(({
              request: request_1,
              events
            }) => {
              if (events.length === 0) {
                addToast('info', 'Retried but no event was generated. Please check your rules.');
              } else {
                addToast('success', 'Retried and a new event was created successfully.');
                mutateContext({
                  event: events[0],
                  request: request_1
                });
              }
            });
          }} />
              </Div>, null]
      };
    }
    const event_1 = event_or_ignored_event;
    return {
      id: event_1.id,
      selected: !!selected_event && event_1.id === selected_event.id,
      fields: [<DisplayDate key={event_1.id} date={event_1.created_at} />, <Div key={`${event_1.id}-status`} flex={{
        gap: 2,
        justify: 'space-between',
        align: 'center'
      }}>
              <Status key={event_1.id} {...event_1} />
              {event_1.status !== 'QUEUED' && webhook && <Button.Permission role="member" ghost icon="retry" muted m={{
          y: -1.5
        }} onClick={e_0 => {
          setSelectedEventId(event_1.id);
          e_0.stopPropagation();
          HookdeckAPI.events.retry(event_1.id).then(updated_event => {
            addToast('success', 'Retrying event delivery');
            mutateContext({
              event: updated_event
            });
          });
        }} />}
            </Div>, event_1.next_attempt_at && <DisplayDate date={event_1.next_attempt_at} />]
    };
  }) || [];
  return <>
      <Div flex={{
      gap: 6
    }}>
        <Div w={{
        px: GUIDED_CONNECTION_WIDTHS.step_container
      }} style={{
        flexShrink: 0
      }}>
          <StyledCard overflow_hidden data-testid="guided-preview-events-panel">
            {rows.length > 0 ? <StyledCardSection data-testid="guided-preview-events-table">
                <Table widths={[1 / 3, 1 / 3, 1 / 4]} headers={['Event Date', 'Event Status', 'Next Delivery Attempt']} rows={rows} onRowSelected={row_id => setSelectedEventId(row_id || null)} table_body_style={{
              overflow: 'auto'
            }} table_body_h={{
              px: 355
            }} />
              </StyledCardSection> : <StyledCardSection muted flex={{
            align: 'center',
            justify: 'center',
            direction: 'column',
            grow: true
          }} style={{
            position: 'relative'
          }} h={{
            px: 398
          }}>
                <Icon icon="loading" m={{
              b: 2
            }} />
                <Text semi>Events will populate here</Text>
                <Text muted center w={{
              px: CONTAINER_WIDTHS.small
            }}>
                  The inbound HTTP requests that Hookdeck sends as outbound events to your
                  destination will appear here.
                </Text>
              </StyledCardSection>}
          </StyledCard>
        </Div>
        {selected_event && <StyledCard w={{
        px: GUIDED_CONNECTION_WIDTHS.details_container
      }} h={{
        px: 386
      }} flex={{
        direction: 'column',
        grow: true
      }} overflow_hidden>
            <StyledCardSection muted p={{
          y: 1,
          x: 2
        }}>
              <Text muted semi size="xs">
                Event Details
              </Text>
            </StyledCardSection>
            {selected_event.error_code ? <>
                <StyledCardSection p={{
            x: 4,
            y: 3
          }}>
                  <Text semi size="s" m={{
              b: 1
            }}>
                    Error
                  </Text>
                  <Badge danger subtle>
                    {selected_event.error_code}
                  </Badge>
                </StyledCardSection>
                <StyledCardSection p={{
            x: 4,
            y: 3
          }} flex={{
            direction: 'column',
            grow: true
          }}>
                  <Text semi size="s" m={{
              b: 1
            }}>
                    Description
                  </Text>
                  <Text size="s">{getErrorCodeLabels()[selected_event.error_code]}</Text>
                </StyledCardSection>
              </> : <>
                <StyledCardSection muted p={{
            t: 2,
            x: 2
          }} style={{
            overflow: 'visible'
          }}>
                  <Div style={{
              overflowX: 'auto',
              overflowY: 'hidden'
            }}>
                    <Tabs compact border={false} active_tab={active_tab} style={{
                flexShrink: 0
              }} m={{
                b: 0.25
              }} onTabSelected={key => setActiveTab(key as 'headers' | 'request' | 'res_body' | 'metadata')} tabs={[{
                key: 'headers',
                label: <Text semi white_space="nowrap">
                              Request Headers
                            </Text>
              }, {
                key: 'request',
                label: <Text semi white_space="nowrap">
                              Request Body
                            </Text>
              }, {
                key: 'res_body',
                label: <Text semi white_space="nowrap">
                              Response Body
                            </Text>
              }, {
                key: 'metadata',
                label: <Text semi white_space="nowrap">
                              Metadata
                            </Text>
              }]} />
                  </Div>
                </StyledCardSection>
                {active_tab === 'metadata' && <StyledCardSection p={{
            x: 4
          }} m={{
            b: -0.25
          }} flex={{
            grow: true,
            direction: 'column'
          }} style={{
            overflow: 'auto'
          }}>
                    <EventMetadata embedded event={selected_event} webhook={webhook} attempts={attempt_results} onAttemptSelected={() => undefined} />
                  </StyledCardSection>}
                {active_tab === 'headers' && <StyledCardSection muted p={4} flex={{
            grow: true,
            direction: 'column'
          }} style={{
            overflow: 'auto'
          }}>
                    <HeadersBlock headers={selected_event.data?.headers || {}} search_value="" />
                  </StyledCardSection>}
                {active_tab === 'request' && <StyledCardSection p={4} muted flex={{
            grow: true,
            direction: 'column'
          }} style={{
            overflow: 'auto'
          }}>
                    {attempt_to_fetch && !attempt || attempt?.body === 'NOT_AVAILABLE_YET' ? <Skeleton h={{
              px: 200
            }} w={100} loading /> : !attempt ? <Text muted>
                        Response will be shown once the delivery attempt has been made.
                      </Text> : <JSONBrowser json={selected_event.data?.body as any || ''} compact />}
                  </StyledCardSection>}
                {active_tab === 'res_body' && <StyledCardSection p={4} muted flex={{
            grow: true,
            direction: 'column'
          }} style={{
            overflow: 'auto'
          }}>
                    {attempt_to_fetch && !attempt || attempt?.body === 'NOT_AVAILABLE_YET' ? <Skeleton h={{
              px: 200
            }} w={100} loading /> : !attempt ? <Text muted>
                        Response body will be shown once the delivery attempt has been made.
                      </Text> : <JSONBrowser json={attempt?.body as any || ''} compact />}
                  </StyledCardSection>}
              </>}
          </StyledCard>}
        {select_ignored_event && <Div w={100}>
            <Div m={{
          l: 4
        }}>
              <StyledCard h={{
            px: 400
          }} flex={{
            direction: 'column',
            grow: true
          }}>
                <StyledCardSection muted p={{
              y: 1,
              x: 4
            }}>
                  <Text muted size="s">
                    Ignored Event
                  </Text>
                </StyledCardSection>
                <StyledCardSection p={{
              x: 4,
              y: 3
            }}>
                  <Text semi size="s" m={{
                b: 1
              }}>
                    Cause
                  </Text>
                  <Badge icon="block" neutral mono>
                    {cause_filter_breakdown[select_ignored_event.cause](select_ignored_event).label}
                  </Badge>
                </StyledCardSection>
                <StyledCardSection p={{
              x: 4,
              y: 3
            }} flex={{
              direction: 'column',
              grow: true
            }}>
                  <Text semi size="s" m={{
                b: 1
              }}>
                    Description
                  </Text>
                  <Text size="s">
                    {cause_filter_breakdown[select_ignored_event.cause](select_ignored_event).text}
                  </Text>
                </StyledCardSection>
              </StyledCard>
            </Div>
          </Div>}
      </Div>
      <Div flex={{
      gap: 3
    }} m={{
      t: 8
    }}>
        <Button icon="success" disabled={events_list?.count === 0} to={'/events'}>
          Confirm connection
        </Button>
      </Div>
    </>;
};
export default PreviewEvents;